/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * User Organizations API Client
 * 
 * API functions for users to view and manage their own organization memberships.
 * These endpoints do NOT require admin access - users can manage their own data.
 */

import { createQueryOptions, createMutationFn } from '@shared/lib/api';
import type { 
  UserOrganizationWithDetails,
  OrganizationMember,
  UserOrganization,
  Organization,
} from '@shared/schema/organizations';

/**
 * Response types for organization endpoints
 */
export interface MyOrganizationsResponse {
  organizations: UserOrganizationWithDetails[];
}

export interface OrganizationMembersResponse {
  members: OrganizationMember[];
}

/**
 * Get the current user's organization memberships
 * Returns all organizations the user belongs to with details
 */
export const getMyOrganizations = () =>
  createQueryOptions<UserOrganizationWithDetails[]>('/api/me/organizations');

/**
 * Get members of a specific organization
 * User must be a member of the organization to access this
 */
export const getOrganizationMembers = (organizationId: string) =>
  createQueryOptions<OrganizationMember[]>(`/api/me/organizations/${organizationId}/members`);

/**
 * Set an organization as the current user's default
 * User must be a member of the organization
 */
export const setDefaultOrganization = (organizationId: string) =>
  createMutationFn<UserOrganization, void>(
    `/api/me/organizations/${organizationId}/default`,
    'PUT'
  );

/**
 * Update an organization's details (name, description)
 * Only organization owners can call this endpoint
 */
export interface UpdateOrganizationData {
  name?: string;
  description?: string;
}

export const updateOrganization = (organizationId: string) =>
  createMutationFn<Organization, UpdateOrganizationData>(
    `/api/organizations/${organizationId}`,
    'PUT'
  );

